import React from 'react';

import css from './SectionNextminePrivacy.module.css';

// Nextmine - Privacy Policy, formateret læsbart.
const SectionNextminePrivacy = () => {
  return (
    <div className={css.root}>
      <div className={css.eyebrow}>Nextmine</div>
      <h1 className={css.title}>Privacy Policy</h1>
      <div className={css.meta}>Last updated / effective date: 15 July 2026</div>

      <div className={css.summary}>
        <p className={css.summaryTitle}>At a glance</p>
        <ul>
          <li>OMJ (Denmark) is the data controller for the Nextmine marketplace.</li>
          <li>We collect account, identity, transaction, payment and usage data - to run the marketplace and meet legal duties.</li>
          <li>We never sell your data. We share it only with processors (Sharetribe, Stripe, Google) and where the law requires.</li>
          <li>Payments and identity checks are handled securely by Stripe - we don’t store full card numbers.</li>
          <li>You have GDPR rights (access, correction, deletion and more). Email <a href="mailto:hi@nextminejewelry.com">hi@nextminejewelry.com</a>.</li>
          <li>This is a plain-language summary only - the full policy below applies.</li>
        </ul>
      </div>
      <hr className={css.hairline} />

      <p className={css.p}>
        This Privacy Policy explains how OMJ (“We”, “Us”, “Our”) collects, uses, shares and protects
        personal data when you use the Nextmine marketplace (the “Service”). It also explains your
        rights and how to exercise them. Please read it together with our Terms of Service.
      </p>

      <h2 className={css.h2}>1 · Who we are (data controller)</h2>
      <p className={css.p}>The data controller responsible for your personal data is:</p>
      <p className={css.contact}>
        OMJ, Poul Anker Bechs Vej 302, 9200 Aalborg SV, Denmark · CVR 30860403 · Email:{' '}
        <a href="mailto:hi@nextminejewelry.com">hi@nextminejewelry.com</a>
      </p>
      <p className={css.p}>
        If you have any question about this Policy or your personal data, contact us at the email
        above.
      </p>

      <h2 className={css.h2}>2 · The personal data we collect</h2>
      <p className={css.p}>Depending on how you use the Service, we may collect:</p>
      <ul className={css.list}>
        <li><span className={css.term}>Account and profile data:</span> name, email, phone number, postal address, username, password (stored hashed), and profile details you provide.</li>
        <li><span className={css.term}>Identity verification data:</span> where required (including by law and our Payment Processor), identity documents and verification information, possibly including government-issued ID. Verification is generally carried out by our Payment Processor; we may receive only the result and limited related data.</li>
        <li><span className={css.term}>Seller financial and tax data:</span> for sellers, bank account details and - for high-volume sellers - tax identification number and verified contact information, as required by the U.S. INFORM Consumers Act and our Payment Processor.</li>
        <li><span className={css.term}>Transaction and order data:</span> goods listed, purchased or sold; order numbers; amounts; shipping and tracking; and order-related correspondence.</li>
        <li><span className={css.term}>Payment data:</span> card and bank details are collected and processed directly by our Payment Processor (Stripe, via Sharetribe). We do not store full card numbers on our own systems.</li>
        <li><span className={css.term}>Communications:</span> messages you send through the Service, and emails or support requests you send us.</li>
        <li><span className={css.term}>Technical and usage data:</span> IP address, device and browser information, log data, and how you interact with the Service.</li>
      </ul>

      <h2 className={css.h2}>3 · How we collect your data</h2>
      <ul className={css.list}>
        <li><span className={css.term}>Directly from you</span> - when you create an account, list or buy goods, complete verification, communicate, or contact support.</li>
        <li><span className={css.term}>Automatically</span> - through cookies and similar technologies (see Section 9).</li>
        <li><span className={css.term}>From third parties</span> - from our Payment Processor (e.g. verification results and payment confirmations) and service providers that help operate the Service.</li>
      </ul>

      <h2 className={css.h2}>4 · Why we use your data and our legal basis</h2>
      <p className={css.p}>
        We process personal data only where we have a legal basis under the EU GDPR. The main bases:
      </p>
      <table className={css.table}>
        <thead>
          <tr>
            <th>Purpose</th>
            <th>Legal basis (GDPR Art. 6)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Creating and managing your account; providing the Service; facilitating orders between buyers and sellers</td>
            <td>Performance of a contract (Art. 6(1)(b))</td>
          </tr>
          <tr>
            <td>Processing payments and payouts via our Payment Processor</td>
            <td>Performance of a contract (Art. 6(1)(b))</td>
          </tr>
          <tr>
            <td>Verifying identity; complying with anti-fraud, tax, sanctions and INFORM Consumers Act obligations</td>
            <td>Legal obligation (Art. 6(1)(c)) and/or legitimate interests (Art. 6(1)(f))</td>
          </tr>
          <tr>
            <td>Preventing fraud and abuse; securing the Service; resolving disputes</td>
            <td>Legitimate interests (Art. 6(1)(f))</td>
          </tr>
          <tr>
            <td>Responding to your complaints and support requests</td>
            <td>Performance of a contract / legitimate interests</td>
          </tr>
          <tr>
            <td>Sending service and transactional messages</td>
            <td>Performance of a contract / legitimate interests</td>
          </tr>
          <tr>
            <td>Sending marketing communications (where applicable)</td>
            <td>Consent (Art. 6(1)(a)), which you may withdraw at any time</td>
          </tr>
          <tr>
            <td>Using analytics and other non-essential cookies</td>
            <td>Consent (Art. 6(1)(a))</td>
          </tr>
        </tbody>
      </table>
      <p className={css.p}>
        Where we rely on legitimate interests, we balance those interests against your rights and
        freedoms. You may object to such processing (see Section 8).
      </p>

      <h2 className={css.h2}>5 · Who we share your data with</h2>
      <p className={css.p}>We do not sell your personal data. We share it only as necessary, with:</p>
      <ul className={css.list}>
        <li><span className={css.term}>Other users:</span> to complete a transaction, a buyer and seller receive the information needed to fulfil and ship an order (e.g. shipping name, address and contact details), to be used only for that order. For high-volume sellers, certain information may be disclosed to buyers as required by the INFORM Consumers Act.</li>
        <li>
          <span className={css.term}>Service providers</span> (processors) acting on our instructions, including:
          <ul className={css.list}>
            <li><span className={css.term}>Sharetribe</span> - marketplace platform, hosting and transaction infrastructure.</li>
            <li><span className={css.term}>Stripe</span> - payment processing, payouts, and identity/financial verification.</li>
            <li><span className={css.term}>Google (Gmail / Workspace)</span> - email and support correspondence.</li>
            <li><span className={css.term}>Google Analytics 4</span> - usage analytics to improve the Service.</li>
          </ul>
        </li>
        <li><span className={css.term}>Authorities and advisers:</span> where required by law or legal process, or to establish, exercise or defend legal claims.</li>
        <li><span className={css.term}>In a business transfer:</span> if OMJ is involved in a merger, acquisition or sale of assets, data may be transferred as part of that transaction, subject to this Policy.</li>
      </ul>

      <h2 className={css.h2}>6 · International transfers</h2>
      <p className={css.p}>
        Because our users are located outside the EU (primarily in the United States and Asia) and
        some service providers operate outside the EU/EEA, your personal data will be transferred
        internationally.
      </p>
      <p className={css.p}>
        Where we transfer personal data outside the EU/EEA, we rely on an appropriate transfer
        mechanism under GDPR - such as the European Commission’s Standard Contractual Clauses or an
        adequacy decision - together with appropriate safeguards. Contact us for more information
        about the safeguards in place.
      </p>

      <h2 className={css.h2}>7 · How long we keep your data</h2>
      <p className={css.p}>We keep personal data only as long as necessary for the purposes above, including:</p>
      <ul className={css.list}>
        <li><span className={css.term}>Account data</span> - while your account is active, and for a reasonable period afterwards.</li>
        <li><span className={css.term}>Transaction, tax and accounting records</span> - for the period required by bookkeeping and tax law (under Danish law, generally 5 years from the end of the relevant financial year).</li>
        <li><span className={css.term}>Verification and anti-fraud records</span> - for as long as required to meet our legal obligations and defend legal claims.</li>
      </ul>
      <p className={css.p}>When data is no longer needed, we delete or anonymise it.</p>

      <h2 className={css.h2}>8 · Your rights</h2>
      <p className={css.p}>Subject to applicable law, you have the right to:</p>
      <ul className={css.list}>
        <li><span className={css.term}>Access</span> - a copy of the personal data we hold about you.</li>
        <li><span className={css.term}>Rectification</span> - correction of inaccurate or incomplete data.</li>
        <li><span className={css.term}>Erasure</span> - deletion of your data, where no overriding legal reason requires us to keep it.</li>
        <li><span className={css.term}>Restriction</span> - to limit how we use your data in certain cases.</li>
        <li><span className={css.term}>Portability</span> - to receive certain data in a structured, machine-readable format.</li>
        <li><span className={css.term}>Objection</span> - to processing based on legitimate interests, and to direct marketing at any time.</li>
        <li><span className={css.term}>Withdraw consent</span> - at any time, without affecting prior processing.</li>
      </ul>
      <p className={css.p}>
        To exercise any of these rights, contact us at <a href="mailto:hi@nextminejewelry.com">hi@nextminejewelry.com</a>.
        We will respond within the time limits required by law and may need to verify your identity
        first.
      </p>
      <p className={css.p}>
        <span className={css.term}>Complaints:</span> if you believe we have not handled your data
        lawfully, you may lodge a complaint with the Danish Data Protection Agency (Datatilsynet, Carl
        Jacobsens Vej 35, 2500 Valby, Denmark, dt@datatilsynet.dk), without prejudice to any other
        remedy.
      </p>

      <h2 className={css.h2}>9 · Cookies and similar technologies</h2>
      <p className={css.p}>
        The Service uses cookies and similar technologies to operate, secure, improve and measure use
        of the Service. We use two types:
      </p>
      <ul className={css.list}>
        <li><span className={css.term}>Strictly necessary cookies</span> - required for the Service to function (e.g. to keep you logged in and secure transactions). These do not require consent.</li>
        <li><span className={css.term}>Analytics cookies</span> - used to understand how visitors use the Service so we can improve it. We set these only with your consent, which you can give or withdraw at any time via our cookie banner or settings. Declining them does not affect your ability to use the Service.</li>
      </ul>
      <p className={css.p}>
        We obtain consent for non-essential cookies through a banner shown on your first visit, and
        you can change your choice at any time via the cookie settings link on the Service.
      </p>

      <h2 className={css.h2}>10 · Security</h2>
      <p className={css.p}>
        We take reasonable technical and organisational measures to protect personal data against
        loss, misuse and unauthorised access, including relying on the security measures of our
        Payment Processor and platform provider. No method of transmission or storage is completely
        secure, and we cannot guarantee absolute security.
      </p>

      <h2 className={css.h2}>11 · Children</h2>
      <p className={css.p}>
        The Service is not directed to, and may not be used by, anyone under the age of majority
        applicable under our Terms of Service. We do not knowingly collect personal data from
        children. If you believe a child has provided us with personal data, contact us and we will
        take appropriate steps to delete it.
      </p>

      <h2 className={css.h2}>12 · Changes to this Privacy Policy</h2>
      <p className={css.p}>
        We may update this Privacy Policy from time to time. If we make material changes, we will take
        reasonable steps to notify you - for example by posting a notice on the Service or contacting
        you. The “Last updated” date at the top shows when the Policy was last revised. Your continued
        use of the Service after changes take effect means you accept the updated Policy.
      </p>

      <h2 className={css.h2}>13 · Contact us</h2>
      <p className={css.contact}>
        For any question about this Privacy Policy or how we handle your personal data, contact us at:
        <br />
        OMJ - <a href="mailto:hi@nextminejewelry.com">hi@nextminejewelry.com</a> · Poul Anker Bechs Vej 302, 9200
        Aalborg SV, Denmark
      </p>

      <hr className={css.hairline} />
    </div>
  );
};

export default SectionNextminePrivacy;
